import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Panel, Loading, SectionLabel } from "@/components/app/primitives";
import { Check } from "lucide-react";

export default function BrandSystem() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/brand-system").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading label="Loading brand system" />;
  if (!data) return null;

  return (
    <div className="max-w-4xl space-y-5">
      <div className="rounded-md border border-border bg-card p-6">
        <SectionLabel>Living reference</SectionLabel>
        <h2 className="mt-2 font-display text-2xl text-foreground">{data.title}</h2>
        <p className="mt-2 text-sm text-muted-foreground leading-6">{data.intro}</p>
      </div>

      <Panel title="Voice and register" testId="brand-voice">
        <div className="p-5">
          <p className="text-base text-foreground leading-7">{data.voice?.register}</p>
        </div>
      </Panel>

      <Panel title="Copy rules" testId="brand-rules">
        <ul className="divide-y divide-border">
          {(data.rules || []).map((r, i) => (
            <li key={i} className="flex items-start gap-3 px-5 py-4">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[rgba(43,79,216,0.18)]">
                <Check className="h-3 w-3 text-[#9DB4FF]" strokeWidth={2.5} />
              </span>
              <div>
                <p className="text-sm font-medium text-foreground">{r.rule}</p>
                <p className="mt-0.5 text-sm text-muted-foreground leading-6">{r.detail}</p>
              </div>
            </li>
          ))}
        </ul>
      </Panel>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Panel title="Palette" testId="brand-palette">
          <div className="p-5 space-y-4">
            <div className="flex items-center gap-3">
              <span className="h-8 w-8 rounded-md border border-border bg-[#0B0D10]" />
              <div><p className="text-sm text-foreground">Base</p><p className="text-xs text-muted-foreground">{data.palette?.base}</p></div>
            </div>
            <div className="flex items-center gap-3">
              <span className="h-8 w-8 rounded-md border border-border bg-primary" />
              <div><p className="text-sm text-foreground">Accent</p><p className="text-xs text-muted-foreground font-mono">{data.palette?.accent}</p></div>
            </div>
            <div className="flex items-center gap-3">
              {["#22C55E", "#F59E0B", "#EF4444"].map((c, i) => (
                <span key={c} className="flex flex-col items-center gap-1">
                  <span className="h-7 w-7 rounded-md border border-border" style={{ background: c }} />
                  <span className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">{(data.palette?.health || [])[i]}</span>
                </span>
              ))}
            </div>
          </div>
        </Panel>

        <Panel title="Type" testId="brand-type">
          <div className="p-5 space-y-4">
            <div>
              <SectionLabel>Interface</SectionLabel>
              <p className="mt-1 text-2xl text-foreground" style={{ fontFamily: "Manrope" }}>{data.type?.interface}</p>
            </div>
            <div>
              <SectionLabel>Figures</SectionLabel>
              <p className="mt-1 font-mono text-lg text-foreground">{data.type?.figures}</p>
            </div>
            <div>
              <SectionLabel>Editorial</SectionLabel>
              <p className="mt-1 font-display text-xl text-foreground">{data.type?.editorial}</p>
            </div>
          </div>
        </Panel>
      </div>
    </div>
  );
}
